import React, { useEffect, useCallback } from 'react';
import { Modal, ActionIcon, Image, Text, Box, rem } from '@mantine/core';
import { IconX, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from '../context/TranslationContext';

export default function ImageLightbox({
  opened,
  onClose,
  images = [],
  selectedIndex = 0,
  onSelectIndex,
}) {
  const { t } = useTranslation();

  const safeImages = Array.isArray(images) ? images : [];
  const hasMultiple = safeImages.length > 1;
  const current = safeImages[selectedIndex];

  const goNext = useCallback(() => {
    if (!hasMultiple || !onSelectIndex) return;
    onSelectIndex((selectedIndex + 1) % safeImages.length);
  }, [hasMultiple, onSelectIndex, selectedIndex, safeImages.length]);

  const goPrev = useCallback(() => {
    if (!hasMultiple || !onSelectIndex) return;
    onSelectIndex((selectedIndex - 1 + safeImages.length) % safeImages.length);
  }, [hasMultiple, onSelectIndex, selectedIndex, safeImages.length]);

  useEffect(() => {
    if (!opened) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [opened, onClose, goNext, goPrev]);

  if (!current) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      padding={0}
      transitionProps={{ transition: 'fade', duration: 200 }}
      withCloseButton={false}
      styles={{
        content: { backgroundColor: 'rgba(0,0,0,0.9)' },
        body: {
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
      aria-label={t('imageViewer', 'Image viewer')}
    >
      <Box pos="fixed" top={rem(20)} right={rem(20)} style={{ zIndex: 1000 }}>
        <ActionIcon
          variant="transparent"
          color="white"
          size="xl"
          onClick={onClose}
          aria-label={t('closeGallery', 'Close gallery')}
        >
          <IconX size={30} aria-hidden="true" />
        </ActionIcon>
      </Box>

      {hasMultiple && (
        <>
          <ActionIcon
            pos="fixed"
            left={rem(20)}
            top="50%"
            variant="transparent"
            color="white"
            size={60}
            onClick={goPrev}
            aria-label={t('previousImage', 'Previous image')}
            style={{ zIndex: 1000 }}
          >
            <IconChevronLeft size={40} aria-hidden="true" />
          </ActionIcon>

          <ActionIcon
            pos="fixed"
            right={rem(20)}
            top="50%"
            variant="transparent"
            color="white"
            size={60}
            onClick={goNext}
            aria-label={t('nextImage', 'Next image')}
            style={{ zIndex: 1000 }}
          >
            <IconChevronRight size={40} aria-hidden="true" />
          </ActionIcon>
        </>
      )}

      <Box p="xl" maw="90vw" mah="85vh">
        <Image
          src={current.src}
          alt={current.alt || ''}
          fit="contain"
          radius="md"
          style={{ maxHeight: '85vh' }}
        />
        {hasMultiple && (
          <Text c="white" ta="center" mt="md" fw={500}>
            {selectedIndex + 1} / {safeImages.length}
          </Text>
        )}
      </Box>
    </Modal>
  );
}
